// $1 Unistroke Recognizer (Wobbrock, Wilson & Li, UIST 2007), compact TypeScript version.
// Runs entirely on the device: a few kilobytes, no model download, no server.

export interface Point { x: number; y: number }
export interface Match { name: string; score: number }

const N = 64;
const SIZE = 250;
const HALF_DIAGONAL = 0.5 * Math.sqrt(2 * SIZE * SIZE);
const ANGLE_RANGE = (45 * Math.PI) / 180;
const ANGLE_PRECISION = (2 * Math.PI) / 180;
const PHI = 0.5 * (-1 + Math.sqrt(5));

const dist = (a: Point, b: Point) => Math.hypot(b.x - a.x, b.y - a.y);
const pathLength = (pts: Point[]) => pts.slice(1).reduce((d, p, i) => d + dist(pts[i], p), 0);
const centroid = (pts: Point[]): Point => ({ x: pts.reduce((s, p) => s + p.x, 0) / pts.length, y: pts.reduce((s, p) => s + p.y, 0) / pts.length });

function resample(points: Point[], n: number): Point[] {
  const pts = points.map((p) => ({ ...p }));
  const interval = pathLength(pts) / (n - 1);
  let D = 0;
  const out: Point[] = [pts[0]];
  for (let i = 1; i < pts.length; i++) {
    const d = dist(pts[i - 1], pts[i]);
    if (D + d >= interval && d > 0) {
      const q = { x: pts[i - 1].x + ((interval - D) / d) * (pts[i].x - pts[i - 1].x), y: pts[i - 1].y + ((interval - D) / d) * (pts[i].y - pts[i - 1].y) };
      out.push(q); pts.splice(i, 0, q); D = 0;
    } else D += d;
  }
  while (out.length < n) out.push(pts[pts.length - 1]);
  return out.slice(0, n);
}

function rotateBy(pts: Point[], a: number): Point[] {
  const c = centroid(pts), cos = Math.cos(a), sin = Math.sin(a);
  return pts.map((p) => ({ x: (p.x - c.x) * cos - (p.y - c.y) * sin + c.x, y: (p.x - c.x) * sin + (p.y - c.y) * cos + c.y }));
}

function normalize(points: Point[]): Point[] {
  let pts = resample(points, N);
  const c = centroid(pts);
  pts = rotateBy(pts, -Math.atan2(c.y - pts[0].y, c.x - pts[0].x));
  const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
  const w = Math.max(...xs) - Math.min(...xs) || 1, h = Math.max(...ys) - Math.min(...ys) || 1;
  pts = pts.map((p) => ({ x: p.x * (SIZE / w), y: p.y * (SIZE / h) }));
  const c2 = centroid(pts);
  return pts.map((p) => ({ x: p.x - c2.x, y: p.y - c2.y }));
}

const pathDistance = (a: Point[], b: Point[]) => a.reduce((d, p, i) => d + dist(p, b[i]), 0) / a.length;

function distanceAtBestAngle(pts: Point[], tpl: Point[]): number {
  let a = -ANGLE_RANGE, b = ANGLE_RANGE;
  let x1 = PHI * a + (1 - PHI) * b, f1 = pathDistance(rotateBy(pts, x1), tpl);
  let x2 = (1 - PHI) * a + PHI * b, f2 = pathDistance(rotateBy(pts, x2), tpl);
  while (Math.abs(b - a) > ANGLE_PRECISION) {
    if (f1 < f2) { b = x2; x2 = x1; f2 = f1; x1 = PHI * a + (1 - PHI) * b; f1 = pathDistance(rotateBy(pts, x1), tpl); }
    else { a = x1; x1 = x2; f1 = f2; x2 = (1 - PHI) * a + PHI * b; f2 = pathDistance(rotateBy(pts, x2), tpl); }
  }
  return Math.min(f1, f2);
}

// --- Templates, generated from simple shapes -------------------------------------------------
function polygon(corners: Point[], closed = true): Point[] {
  const pts = closed ? [...corners, corners[0]] : corners;
  const out: Point[] = [];
  for (let i = 0; i < pts.length - 1; i++) for (let t = 0; t < 1; t += 0.05) out.push({ x: pts[i].x + (pts[i + 1].x - pts[i].x) * t, y: pts[i].y + (pts[i + 1].y - pts[i].y) * t });
  out.push(pts[pts.length - 1]);
  return out;
}
const rotations = <T>(arr: T[]) => arr.map((_, i) => [...arr.slice(i), ...arr.slice(0, i)]);

function circle(cw: boolean, start = 0): Point[] {
  return Array.from({ length: 64 }, (_, i) => { const a = start + (cw ? 1 : -1) * (i / 63) * 2 * Math.PI; return { x: Math.cos(a) * 100, y: Math.sin(a) * 100 }; });
}
function spiral(): Point[] {
  return Array.from({ length: 80 }, (_, i) => { const a = (i / 79) * 4 * Math.PI, r = 10 + i * 1.2; return { x: Math.cos(a) * r, y: Math.sin(a) * r }; });
}

const TRI: Point[] = [{ x: 0, y: -100 }, { x: 87, y: 50 }, { x: -87, y: 50 }];
const SQUARE: Point[] = [{ x: -100, y: -100 }, { x: 100, y: -100 }, { x: 100, y: 100 }, { x: -100, y: 100 }];

const RAW: Array<[string, Point[]]> = [
  ...rotations(TRI).map((c): [string, Point[]] => ['driehoek', polygon(c)]),
  ...rotations([...TRI].reverse()).map((c): [string, Point[]] => ['driehoek', polygon(c)]),
  ...rotations(SQUARE).slice(0, 2).map((c): [string, Point[]] => ['vierkant', polygon(c)]),
  ...rotations([...SQUARE].reverse()).slice(0, 2).map((c): [string, Point[]] => ['vierkant', polygon(c)]),
  ['cirkel', circle(true)], ['cirkel', circle(false)], ['cirkel', circle(true, Math.PI / 2)], ['cirkel', circle(false, -Math.PI / 2)],
  ['V-vorm', polygon([{ x: -80, y: -80 }, { x: 0, y: 80 }, { x: 80, y: -80 }], false)],
  ['vinkje', polygon([{ x: -60, y: 0 }, { x: -10, y: 60 }, { x: 80, y: -80 }], false)],
  ['zigzag', polygon([{ x: -100, y: -40 }, { x: -50, y: 40 }, { x: 0, y: -40 }, { x: 50, y: 40 }, { x: 100, y: -40 }], false)],
  ['spiraal', spiral()],
];
const TEMPLATES = RAW.map(([name, pts]) => ({ name, pts: normalize(pts) }));

export function recognize(points: Point[]): Match {
  if (points.length < 8) return { name: 'te kort', score: 0 };
  const pts = normalize(points);
  let best = Infinity, name = '';
  for (const t of TEMPLATES) {
    const d = distanceAtBestAngle(pts, t.pts);
    if (d < best) { best = d; name = t.name; }
  }
  return { name, score: 1 - best / HALF_DIAGONAL };
}
